import { useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import * as HomeStore from '../../stores/HomeStore'
import * as WorkoutStore from '../../stores/WorkoutStore'
import { db, type CustomExerciseTemplate, type LocalSession } from '@/lib/db'
import { toast } from '@/components/function/base/Toaster'

const BJ_OFFSET_MS = 8 * 60 * 60 * 1000

function isoToBjDatetimeLocal(iso: string): string {
  const bj = new Date(new Date(iso).getTime() + BJ_OFFSET_MS)
  const y = bj.getUTCFullYear()
  const m = String(bj.getUTCMonth() + 1).padStart(2, '0')
  const d = String(bj.getUTCDate()).padStart(2, '0')
  const hh = String(bj.getUTCHours()).padStart(2, '0')
  const mm = String(bj.getUTCMinutes()).padStart(2, '0')
  return `${y}-${m}-${d}T${hh}:${mm}`
}

function bjDatetimeLocalToDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(s)
  if (!m) return null
  const [, y, mo, d, hh, mm] = m
  const utcMs = Date.UTC(+y, +mo - 1, +d, +hh, +mm) - BJ_OFFSET_MS
  return new Date(utcMs)
}

interface SetEntry {
  reps?: number
  weight?: number
}

interface SessionSummary {
  totalSets?: number
  totalReps?: number
  completedSets?: number
  weight?: number
  sets?: SetEntry[]
  [key: string]: unknown
}

/** True when the session logged per-set data AND the weights weren't all
 *  the same — only then is expanding actually informative. */
function hasMixedWeights(summary: SessionSummary | null): boolean {
  const sets = summary?.sets
  if (!Array.isArray(sets) || sets.length < 2) return false
  const weights = sets
    .map(s => s.weight)
    .filter((w): w is number => typeof w === 'number')
  if (weights.length < 2) return false
  return new Set(weights).size > 1
}

function formatSessionMetric(
  exerciseKey: string,
  durationSeconds: number,
  summary: SessionSummary | null,
  template: CustomExerciseTemplate | null,
): string {
  const s = summary ?? {}
  // Custom exercises: format by template
  if (template) {
    switch (template) {
      case 'timer':
      case 'laps_timer':
        return `${Math.round(durationSeconds / 60)}分钟`
      case 'countdown': {
        const mins = Math.round(durationSeconds / 60)
        if (s.totalSets && mins > 0) return `${s.totalSets}组 ${mins}分钟`
        if (s.totalSets) return `${s.totalSets}组`
        return `${mins}分钟`
      }
      case 'manual_reps': {
        const segs: string[] = []
        if (s.totalSets) segs.push(`${s.totalSets}组`)
        if (s.totalReps) segs.push(`${s.totalReps}次`)
        return segs.length > 0 ? segs.join(' ') : '—'
      }
    }
  }
  // Built-ins
  switch (exerciseKey) {
    case 'plank':
      return `${Math.round(durationSeconds / 60)}分钟`
    case 'push_up':
    case 'squat':
      if (s.totalSets && s.totalReps) return `${s.totalSets}组 ${s.totalReps}次`
      return s.totalReps ? `${s.totalReps}次` : `${Math.round(durationSeconds / 60)}分钟`
    case 'dumbbell_curl':
    case 'dumbbell_press':
    case 'glute_bridge':
      if (s.totalSets && s.totalReps) return `${s.totalSets}组 ${s.totalReps}次`
      if (s.totalSets) return `${s.totalSets}组`
      return `${Math.round(durationSeconds / 60)}分钟`
    default: {
      const mins = Math.round(durationSeconds / 60)
      return mins > 0 ? `${mins}分钟` : `${durationSeconds}秒`
    }
  }
}

function WeeklyDetail() {
  const navigate = useNavigate()
  const weekLabel = useAtomValue(HomeStore.weekLabelAtom)
  const days = useAtomValue(HomeStore.daysAtom)
  const weeklyStats = useAtomValue(HomeStore.weeklyStatsAtom)
  const breakdownItems = weeklyStats.anaerobic.items
  const deleteSession = useSetAtom(WorkoutStore.deleteWorkoutSessionAtom)
  const editAerobicSession = useSetAtom(WorkoutStore.editAerobicSessionAtom)
  const [isEditing, setIsEditing] = useState(false)
  const [editingAerobic, setEditingAerobic] = useState<LocalSession | null>(null)
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(() => new Set())
  const toggleExpand = (id: string) => {
    setExpandedSessions(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const handleDelete = (sessionId: string, label: string) => {
    if (confirm(`删除「${label}」这条训练记录？已同步到云端的也会一并删除。`)) {
      void deleteSession(sessionId)
    }
  }

  const handleOpenEdit = (sessionId: string) => {
    void (async () => {
      const row = await db.sessions.get(sessionId)
      if (!row) return
      setEditingAerobic(row)
    })()
  }

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: '#12141D' }}>
      {/* Header */}
      <div className="flex-none pt-12 pb-2 px-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: '#1C1F2E' }}
          >
            <Icon icon="lucide:arrow-left" className="w-5 h-5 text-gray-300" />
          </button>
          <h1 className="text-xl font-bold text-white flex-1 text-center">周运动详情</h1>
          <button
            onClick={() => setIsEditing(!isEditing)}
            aria-label={isEditing ? '完成' : '修改'}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: isEditing ? '#10B981' : '#1C1F2E' }}
          >
            <Icon
              icon={isEditing ? 'lucide:check' : 'lucide:pencil-line'}
              className={`w-4 h-4 ${isEditing ? 'text-white' : 'text-gray-400'}`}
            />
          </button>
        </div>
        {weekLabel && (
          <p className="text-center text-sm text-gray-500 mt-1">{weekLabel}</p>
        )}
      </div>

      {/* Day list */}
      <main className="flex-1 overflow-y-auto px-6 pt-4 pb-12 flex flex-col gap-4">
        {/* Weekly summary — moved here from Home so the home stats stay slim */}
        {(weeklyStats.aerobicMinutes > 0 || breakdownItems.length > 0) && (
          <section
            className="rounded-2xl px-4 py-3 flex flex-col gap-1.5"
            style={{ backgroundColor: '#1C1F2E' }}
          >
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">本周汇总</h3>
            {weeklyStats.aerobicMinutes > 0 && (
              <p className="text-sm text-gray-400 leading-relaxed">
                <span className="text-emerald-400 font-medium">有氧</span>{' '}
                <span className="text-white font-bold">{weeklyStats.aerobicMinutes}</span> 分钟
              </p>
            )}
            {breakdownItems.length > 0 && (
              <div className="flex flex-col gap-0.5">
                {breakdownItems.map((it) => {
                  const segs: string[] = []
                  if (it.sets && it.sets > 0) segs.push(`${it.sets} 组`)
                  if (it.reps && it.reps > 0) segs.push(`${it.reps} 次`)
                  if (it.minutes && it.minutes > 0) segs.push(`${it.minutes} 分钟`)
                  return (
                    <p key={it.exerciseKey} className="text-sm text-gray-400 leading-relaxed">
                      <span className="text-violet-400">{it.label}</span>{' '}
                      <span className="text-white font-bold">{segs.join(' ')}</span>
                    </p>
                  )
                })}
              </div>
            )}
          </section>
        )}

        {days.map((day, i) => (
          <div key={i} className={day.isFuture ? 'opacity-40' : ''}>
            {/* Day header */}
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-2 h-2 rounded-full flex-none"
                style={{
                  backgroundColor: day.isToday ? '#10B981' : day.isFuture ? '#4B5563' : '#6B7280',
                }}
              />
              <span className={`text-sm ${day.isToday ? 'text-white font-bold' : 'text-gray-400'}`}>
                {day.dayOfWeek} · {day.date}
                {day.isToday && <span className="text-emerald-400 ml-1">（今天）</span>}
              </span>
            </div>

            {/* Sessions card */}
            <div
              className="rounded-2xl px-4 py-3"
              style={{ backgroundColor: '#1C1F2E' }}
            >
              {day.sessions.length > 0 ? (
                day.sessions.map((s, j) => {
                  const summary = s.summary as SessionSummary | null
                  const mixed = hasMixedWeights(summary)
                  const expanded = expandedSessions.has(s.id)
                  const hideAggregateWeight = mixed  // skip "-5kg" suffix when weights vary
                  return (
                    <div
                      key={j}
                      className={j < day.sessions.length - 1 ? 'border-b border-[#2A2D3A]' : ''}
                    >
                      <div className="flex items-center gap-3 py-2">
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-none"
                          style={{
                            backgroundColor: s.exerciseType === 'aerobic' ? '#10B981' : '#8B5CF6',
                          }}
                        />
                        <span className="text-sm text-white font-medium flex-1 truncate">
                          {s.label}
                          {(() => {
                            if (hideAggregateWeight) return ''
                            const w = summary?.weight
                            return typeof w === 'number' && w > 0 ? `-${w}kg` : ''
                          })()}
                        </span>
                        {mixed && (
                          <button
                            onClick={() => toggleExpand(s.id)}
                            aria-label={expanded ? '收起' : '展开每组重量'}
                            className="w-5 h-5 rounded flex items-center justify-center flex-none text-gray-400"
                          >
                            <Icon icon={expanded ? 'lucide:chevron-up' : 'lucide:chevron-down'} className="w-4 h-4" />
                          </button>
                        )}
                        <span className="text-sm text-white font-bold">
                          {formatSessionMetric(s.exerciseKey, s.durationSeconds, summary, s.template)}
                        </span>
                        <span className="text-xs text-gray-500 font-mono w-12 text-right">
                          {s.startTime}
                        </span>
                        {isEditing && s.exerciseType === 'aerobic' && (
                          <button
                            onClick={() => handleOpenEdit(s.id)}
                            aria-label="补记时间"
                            className="w-6 h-6 rounded-full flex items-center justify-center bg-gray-700/40 flex-none"
                          >
                            <Icon icon="lucide:clock" className="w-3.5 h-3.5 text-gray-300" />
                          </button>
                        )}
                        {isEditing && (
                          <button
                            onClick={() => handleDelete(s.id, s.label)}
                            aria-label="删除"
                            className="w-6 h-6 rounded-full flex items-center justify-center bg-red-500/15 flex-none"
                          >
                            <Icon icon="lucide:trash-2" className="w-3.5 h-3.5 text-red-400" />
                          </button>
                        )}
                      </div>
                      {mixed && expanded && summary?.sets && (
                        <div className="pb-2 pl-5 flex flex-col gap-0.5">
                          {summary.sets.map((set, idx) => (
                            <div key={idx} className="text-xs text-gray-400 font-mono flex gap-3">
                              <span className="text-gray-600 w-12">第{idx + 1}组</span>
                              <span className="text-emerald-400">{typeof set.weight === 'number' ? `${set.weight}kg` : '—'}</span>
                              <span className="text-gray-300">{set.reps ?? '—'}次</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })
              ) : day.isFuture ? (
                <p className="text-sm text-gray-600 py-1">—</p>
              ) : (
                <div className="flex items-center gap-2 py-1">
                  <Icon icon="lucide:coffee" className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-600">休息日</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </main>

      {editingAerobic && (
        <EditAerobicModal
          session={editingAerobic}
          onClose={() => setEditingAerobic(null)}
          onSave={async (startedAt, durationSeconds) => {
            try {
              await editAerobicSession({ sessionId: editingAerobic.id, startedAt, durationSeconds })
              toast.success('已更新')
              setEditingAerobic(null)
            } catch {
              toast.error('保存失败')
            }
          }}
        />
      )}
    </div>
  )
}

function EditAerobicModal({ session, onClose, onSave }: {
  session: LocalSession
  onClose: () => void
  onSave: (startedAt: Date, durationSeconds: number) => Promise<void>
}) {
  const [startStr, setStartStr] = useState(() => isoToBjDatetimeLocal(session.startedAt))
  const [minutesStr, setMinutesStr] = useState(() => String(Math.max(1, Math.round(session.durationSeconds / 60))))
  const [saving, setSaving] = useState(false)

  const handleSubmit = () => {
    const startedAt = bjDatetimeLocalToDate(startStr)
    const minutes = parseInt(minutesStr, 10)
    if (!startedAt) { toast.error('开始时间无效'); return }
    if (!minutes || minutes <= 0) { toast.error('时长必须大于 0'); return }
    setSaving(true)
    void onSave(startedAt, minutes * 60).finally(() => setSaving(false))
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 flex flex-col gap-4"
        style={{ backgroundColor: '#1C1F2E' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-white font-bold text-base">补记 / 修改</h3>
        <p className="text-xs text-gray-500 -mt-2">只能修改开始时间和时长，用于补记忘了打卡的训练</p>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-400">开始时间（北京时间）</span>
          <input
            type="datetime-local"
            value={startStr}
            onChange={(e) => setStartStr(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[#0F1119] text-white text-sm border border-[#2A2D3A]"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-400">时长（分钟）</span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            value={minutesStr}
            onChange={(e) => setMinutesStr(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[#0F1119] text-white text-sm border border-[#2A2D3A]"
          />
        </label>

        <div className="flex gap-2 mt-2">
          <button
            onClick={onClose}
            disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-[#2A2D3A] text-gray-300 font-medium text-sm disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold text-sm disabled:opacity-50"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  )
}

export default WeeklyDetail
